import React, { useState, useEffect } from "react";
import api from "../services/api";

const inputStyle = {
  width: "100%",
  padding: "8px 11px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "8px",
  color: "var(--text-primary)",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const sectionTitleStyle = {
  fontSize: "11px",
  fontWeight: "700",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  marginBottom: "12px",
};

function Spinner() {
  return (
    <span
      style={{
        display: "inline-block",
        width: "13px",
        height: "13px",
        border: "2px solid currentColor",
        borderTopColor: "transparent",
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
      }}
    />
  );
}

export function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="wrapper__fb__ratings d-flex mb__10 gap__5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className={star <= (hovered || value) ? "fb__star__rating active": "fb__star__rating "}
          aria-label={`${star} star`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

/**
 * Reusable feedback UI: a star rating that saves instantly on click, plus a
 * free-text note that sends separately (with a modern send button + edit).
 * Used by both the trial popup and the menu-triggered feedback modal.
 */
export default function FeedbackSection({
  savedRating = null,
  noteAlreadySubmitted = false,
  onNoteSubmitted,
  onRatingSaved,
  heading = true,
}) {
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [fbSubmitting, setFbSubmitting] = useState(false);
  const [fbSubmittedLocal, setFbSubmittedLocal] = useState(false);
  const [fbEditing, setFbEditing] = useState(false);
  const [fbError, setFbError] = useState("");

  const fbSubmitted = fbSubmittedLocal || !!noteAlreadySubmitted;

  // Restore a previously-saved rating once it's available.
  useEffect(() => {
    if (typeof savedRating === "number" && savedRating > 0) {
      setRating((r) => (r === 0 ? savedRating : r));
    }
  }, [savedRating]);

  // Star rating saves immediately on click — independent of the text note.
  const saveRating = async (value) => {
    setRating(value);
    onRatingSaved?.(value); // reflect immediately in the shared status
    try {
      await api.submitTrialFeedback({ type: "rating", rating: value });
    } catch (err) {
      /* non-blocking — the click already updated the UI */
    }
  };

  // The text note sends separately via the send button.
  const submitFeedback = async () => {
    if (!feedback.trim()) {
      setFbError("Please write a short note first.");
      return;
    }
    setFbError("");
    setFbSubmitting(true);
    try {
      await api.submitTrialFeedback({ type: "feedback", feedback, rating });
      setFbSubmittedLocal(true);
      setFbEditing(false);
      onNoteSubmitted?.();
    } catch (err) {
      setFbError("Something went wrong. Please try again.");
    } finally {
      setFbSubmitting(false);
    }
  };

  const errorLine = (msg) =>
    msg ? (
      <span className="fb__12 d__block mt__8 fc__E52F0F">
        ⚠ {msg}
      </span>
    ) : null;
  
  const feedbackSendBtn = fbSubmitting || !feedback.trim() ? "": "sendBtnActive";
  return (
    <>
      {heading && <div style={sectionTitleStyle}>Share Your Feedback</div>}

      {/* Star rating — always visible, persists instantly on click */}
      <StarRating value={rating} onChange={saveRating} />

      {/* Text note — collapses on its own once sent, with an Edit option */}
      {fbSubmitted && !fbEditing ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px 14px",
            borderRadius: "10px",
            background: "rgba(16,185,129,0.08)",
            border: "1px solid rgba(16,185,129,0.25)",
          }}
        >
          <span className="fb__16 fc__007A55">✓</span>
          <span className="fb__13 fw__600 fc__007A55">
            Thanks for your feedback!
          </span>
          <button
            type="button"
            onClick={() => {
              setFeedback("");
              setFbError("");
              setFbEditing(true);
            }}
            style={{
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              background: "none",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "7px",
              color: "var(--text-secondary)",
              fontSize: "12px",
              fontWeight: 600,
              padding: "5px 12px",
              cursor: "pointer",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path
                d="M9.5 2.5l2 2L5 11l-2.5.5L3 9l6.5-6.5z"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinejoin="round"
              />
            </svg>
            Edit
          </button>
        </div>
      ) : (
        <>
          <div className="pos__rel">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  submitFeedback();
                }
              }}
              placeholder="Share a couple of thoughts on your experience…"
              rows={4}
              className="form__input"
            />
            <button
              type="button"
              onClick={submitFeedback}
              disabled={fbSubmitting || !feedback.trim()}
              title="Send note"
              aria-label="Send note"
              className={`fb__send ${feedbackSendBtn}`}
            >
              {fbSubmitting ? (
                <Spinner />
              ) : (
                <svg width="17" height="17" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 13V3.5M8 3.5L3.75 7.75M8 3.5l4.25 4.25"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
          {errorLine(fbError)}
          {fbEditing && (
            <button type="button" onClick={() => { setFbEditing(false); setFbError("");}} className="fb__edit__cancel common__btn common__transp__btn mt__8">
              Cancel
            </button>
          )}
        </>
      )}
    </>
  );
}
